import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatApiError, formatPrice, getImageUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Download, Mail, MapPin, Pencil, Phone, RefreshCcw, Search, User } from "lucide-react";

const perPageOptions = [10, 20, 50];
const emptyForm = {
  name: "",
  email: "",
  phone: "",
  address: "",
  postal_code: "",
  role: "retail",
  status: "active",
};

function formatRole(role) {
  if (role === "retail") return "Retail";
  if (role === "reseller") return "Reseller";
  if (role === "agen") return "Agen";
  if (role === "mitra" || role === "produsen") return "Mitra";
  return role || "-";
}

function formatStatus(status) {
  if (status === "active") return "Aktif";
  if (status === "inactive") return "Tidak Aktif";
  return status || "-";
}

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function getFilenameFromDisposition(disposition, fallback) {
  const match = disposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || fallback;
}

function formatMonthKey(value) {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) return value || "-";
  const [year, month] = value.split("-").map(Number);
  return new Intl.DateTimeFormat("id-ID", { month: "long", year: "numeric" }).format(new Date(year, month - 1, 1));
}

export default function AdminCustomerDetail() {
  const navigate = useNavigate();
  const { userId } = useParams();

  const [customer, setCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({ total_nominal: 0, total_orders: 0 });
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, total_pages: 1 });
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState("10");

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  const loadCustomer = async () => {
    if (!userId) return;
    setLoadingCustomer(true);
    try {
      const response = await API.get(`/admin/customer-directory/${userId}`);
      const nextCustomer = response.data?.customer || null;
      setCustomer(nextCustomer);
      return nextCustomer;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      navigate("/admin/customers");
      return null;
    } finally {
      setLoadingCustomer(false);
    }
  };

  const fetchOrders = async (page = currentPage, nextPerPage = perPage) => {
    if (!userId) return;
    setLoadingOrders(true);
    try {
      const params = {
        page,
        per_page: Number(nextPerPage),
      };
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await API.get(`/admin/customer-directory/${userId}/orders`, { params });
      setOrders(response.data?.orders || []);
      setSummary(response.data?.summary || { total_nominal: 0, total_orders: 0 });
      setPagination(response.data?.pagination || { page, per_page: Number(nextPerPage), total: 0, total_pages: 1 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoadingOrders(false);
    }
  };

  useEffect(() => {
    loadCustomer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, perPage]);

  useEffect(() => {
    fetchOrders(currentPage, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, currentPage, perPage, debouncedSearch]);

  const openEdit = () => {
    if (!customer) return;
    setForm({
      name: customer.name || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      postal_code: customer.postal_code || "",
      role: customer.role || "retail",
      status: customer.status || "active",
    });
    setEditOpen(true);
  };

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        postal_code: form.postal_code.trim(),
      };
      const response = await API.put(`/admin/customer-directory/${userId}`, payload);
      setCustomer(response.data?.customer || null);
      setEditOpen(false);
      toast.success("Customer berhasil diperbarui");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    if (!customer) return;
    setExporting(true);
    try {
      const response = await API.get(`/admin/customer-directory/${userId}/orders/export`, {
        responseType: "blob",
      });
      const filename = getFilenameFromDisposition(
        response.headers["content-disposition"],
        `customer-orders-${(customer.name || userId).replace(/\s+/g, "-").toLowerCase()}.csv`
      );
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Order customer berhasil diekspor");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  const handleRefresh = async () => {
    setCurrentPage(1);
    await loadCustomer();
    await fetchOrders(1, perPage);
  };

  return (
    <AdminLayout title="Customer Detail">
      <div className="space-y-6" data-testid="admin-customer-detail">
        {customer?.reward_summary && (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="bg-white border border-stone-100 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Poin Reward Bulan Ini</p>
              <p className="text-3xl font-semibold text-stone-900">{customer.reward_summary.current_month_points || 0}</p>
              <p className="text-xs text-stone-500 mt-2">
                {customer.reward_summary.enabled
                  ? `${formatPrice(customer.reward_summary.point_divisor || 500000)} = 1 poin`
                  : "Poin reward aktif untuk customer partner tertentu."}
              </p>
            </div>
            <div className="bg-white border border-stone-100 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Omzet Bulan Ini</p>
              <p className="text-2xl font-semibold text-stone-900">{formatPrice(customer.reward_summary.current_month_omzet || 0)}</p>
              <p className="text-xs text-stone-500 mt-2">Periode {formatMonthKey(customer.reward_summary.current_month)}</p>
            </div>
            <div className="bg-white border border-stone-100 rounded-lg p-5">
              <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Akumulasi Poin</p>
              <p className="text-2xl font-semibold text-stone-900">{customer.reward_summary.total_points || 0}</p>
              <p className="text-xs text-stone-500 mt-2">Akumulasi dari omzet bulanan reseller.</p>
            </div>
          </div>
        )}
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div>
            <button
              onClick={() => navigate("/admin/customers")}
              className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-stone-700 transition-colors mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Kembali ke daftar customer
            </button>
            <p className="text-sm text-stone-500">
              Detail customer operasional dibaca dari akun aktif dan histori transaksi existing.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefresh}
              className="h-9 px-3 border border-stone-200 rounded-md text-stone-600 hover:bg-stone-50 transition-colors inline-flex items-center gap-2"
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={openEdit}
              disabled={!customer}
              className="h-9 px-4 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50 transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Pencil className="w-4 h-4" />
              Ubah
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || !customer}
              className="h-9 px-4 rounded-md bg-[#8A9A86] text-white hover:bg-[#768573] transition-colors inline-flex items-center gap-2 disabled:opacity-60"
            >
              <Download className="w-4 h-4" />
              {exporting ? "Ekspor..." : "Ekspor Order"}
            </button>
          </div>
        </div>

        {loadingCustomer ? (
          <div className="bg-white border border-stone-100 rounded-lg p-8 text-center text-stone-400">Memuat detail customer...</div>
        ) : customer ? (
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 bg-white border border-stone-100 rounded-lg p-6">
              <div className="flex items-start gap-4">
                {customer.picture ? (
                  <img src={getImageUrl(customer.picture)} alt={customer.name} className="w-16 h-16 rounded-full object-cover shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-stone-100 flex items-center justify-center shrink-0">
                    <User className="w-6 h-6 text-stone-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h2 className="text-xl font-semibold text-stone-900">{customer.name || "Tanpa Nama"}</h2>
                  <p className="text-sm text-stone-500 mt-1">{customer.email || "-"}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-3">
                    <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex bg-stone-100 text-stone-700">
                      {formatRole(customer.role)}
                    </span>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex ${customer.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-stone-100 text-stone-600"}`}>
                      {formatStatus(customer.status)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6 text-sm">
                <div className="border border-stone-100 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Telepon</p>
                  <p className="text-stone-700 inline-flex items-center gap-2"><Phone className="w-4 h-4 text-stone-400" /> {customer.phone || "-"}</p>
                </div>
                <div className="border border-stone-100 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Email</p>
                  <p className="text-stone-700 inline-flex items-center gap-2 break-all"><Mail className="w-4 h-4 text-stone-400" /> {customer.email || "-"}</p>
                </div>
                <div className="border border-stone-100 rounded-lg p-4 md:col-span-2">
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Alamat</p>
                  <p className="text-stone-700 inline-flex items-start gap-2 leading-relaxed"><MapPin className="w-4 h-4 text-stone-400 mt-0.5 shrink-0" /> {customer.address || "-"}</p>
                </div>
                <div className="border border-stone-100 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Kode Pos</p>
                  <p className="text-stone-700">{customer.postal_code || "-"}</p>
                </div>
                <div className="border border-stone-100 rounded-lg p-4">
                  <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Terdaftar</p>
                  <p className="text-stone-700">{formatDate(customer.created_at)}</p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white border border-stone-100 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Total Order</p>
                <p className="text-2xl font-semibold text-stone-900">{customer.total_orders || 0}</p>
              </div>
              <div className="bg-white border border-stone-100 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Total Belanja</p>
                <p className="text-2xl font-semibold text-stone-900">{formatPrice(customer.total_spent || 0)}</p>
              </div>
              <div className="bg-white border border-stone-100 rounded-lg p-5">
                <p className="text-xs uppercase tracking-wider text-stone-400 mb-2">Order Terakhir</p>
                <p className="text-sm font-medium text-stone-700">{formatDate(customer.last_order_date)}</p>
              </div>
            </div>
          </div>
        ) : null}

        {customer?.reward_summary && (
          <div className="bg-white border border-stone-100 rounded-lg overflow-hidden">
            <div className="p-5 border-b border-stone-100">
              <h3 className="text-lg font-semibold text-stone-900">Omzet Partner per Bulan</h3>
              <p className="text-sm text-stone-500 mt-1">
                Dasar poin reward customer partner, dengan rasio {formatPrice(customer.reward_summary.point_divisor || 500000)} = 1 poin.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-stone-100 bg-stone-50/50">
                    <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Bulan</th>
                    <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Jumlah Order</th>
                    <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Omzet</th>
                    <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Poin</th>
                  </tr>
                </thead>
                <tbody>
                  {customer.reward_summary.enabled && customer.reward_summary.monthly_omzet?.length > 0 ? customer.reward_summary.monthly_omzet.map((item) => (
                    <tr key={item.month} className="border-b border-stone-50">
                      <td className="p-4 text-stone-800 font-medium">{formatMonthKey(item.month)}</td>
                      <td className="p-4 text-stone-600">{item.total_orders || 0}</td>
                      <td className="p-4 text-stone-700">{formatPrice(item.omzet || 0)}</td>
                      <td className="p-4">
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex bg-amber-100 text-amber-700">
                          {item.points || 0} poin
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} className="p-8 text-center text-stone-400 italic">
                        {customer.reward_summary.enabled ? "Belum ada omzet partner bulanan." : "Reward point hanya berlaku untuk customer partner tertentu."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="bg-white border border-stone-100 rounded-lg overflow-hidden">
          <div className="p-5 border-b border-stone-100 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-stone-900">Riwayat Order</h3>
              <p className="text-sm text-stone-500 mt-1">
                Total {summary.total_orders || 0} order, nominal {formatPrice(summary.total_nominal || 0)}.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <div className="relative w-full sm:w-72">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-stone-400" />
                </div>
                <Input
                  type="text"
                  placeholder="Cari ID order..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>
              <Select value={perPage} onValueChange={setPerPage}>
                <SelectTrigger className="w-full sm:w-[120px] h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {perPageOptions.map((value) => (
                    <SelectItem key={value} value={String(value)}>{value} / page</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-stone-100 bg-stone-50/50">
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">ID</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Tanggal</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Detail Produk</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Nominal</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Pembayaran</th>
                  <th className="text-left p-4 text-xs font-semibold tracking-wider uppercase text-stone-500">Status Order</th>
                </tr>
              </thead>
              <tbody>
                {loadingOrders ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-stone-400">Memuat order...</td>
                  </tr>
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-stone-400 italic">Belum ada order yang cocok.</td>
                  </tr>
                ) : orders.map((order) => (
                  <tr key={order.transaction_id} className="border-b border-stone-50 hover:bg-stone-50/60 transition-colors">
                    <td className="p-4 font-medium text-stone-800 whitespace-nowrap">{order.transaction_id}</td>
                    <td className="p-4 text-stone-600 whitespace-nowrap">{formatDate(order.transaction_date)}</td>
                    <td className="p-4 text-stone-700 min-w-[220px]">{order.product_summary || "-"}</td>
                    <td className="p-4 text-stone-700 whitespace-nowrap">{formatPrice(order.grand_total || 0)}</td>
                    <td className="p-4">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex bg-blue-50 text-blue-700">
                        {order.payment_status_label || "-"}
                      </span>
                    </td>
                    <td className="p-4">
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded inline-flex bg-stone-100 text-stone-700">
                        {order.order_status_label || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between p-5 border-t border-stone-100">
              <p className="text-sm text-stone-500">
                Halaman {pagination.page} dari {pagination.total_pages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  disabled={pagination.page <= 1}
                  className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage((page) => Math.min(pagination.total_pages, page + 1))}
                  disabled={pagination.page >= pagination.total_pages}
                  className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="bg-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">Ubah Customer</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Nama</Label>
                <Input value={form.name} onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))} className="mt-1" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Kategori</Label>
                  <Select value={form.role} onValueChange={(value) => setForm((prev) => ({ ...prev, role: value }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="reseller">Reseller</SelectItem>
                      <SelectItem value="agen">Agen</SelectItem>
                      <SelectItem value="mitra">Mitra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Status</Label>
                  <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Aktif</SelectItem>
                      <SelectItem value="inactive">Tidak Aktif</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Telepon</Label>
                  <Input value={form.phone} onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-stone-500">Kode Pos</Label>
                  <Input value={form.postal_code} onChange={(e) => setForm((prev) => ({ ...prev, postal_code: e.target.value }))} className="mt-1" />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Alamat</Label>
                <textarea
                  value={form.address}
                  onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
                  rows={3}
                  className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86] rounded-md"
                />
              </div>
              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  onClick={() => setEditOpen(false)}
                  className="px-4 py-2 text-sm rounded-md border border-stone-200 text-stone-600 hover:bg-stone-50 transition-colors"
                >
                  Batal
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm rounded-md bg-[#8A9A86] text-white hover:bg-[#768573] transition-colors disabled:opacity-60"
                >
                  {saving ? "Menyimpan..." : "Simpan Perubahan"}
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
